import { isForeignEnvironment } from '@/lib/pwa';
import { getClientContext } from '@/utils/app-context/client';

// GA4 mirror of the CBCC CleverTap events, ported from fintech-ui's
// `trackGaEvent` (useCbccTracker). Names and param keys are snake_cased the
// same way, so they line up with fintech-ui's GA events and the native apps'
// Firebase ones. Inside a host app's webview this does nothing at all: the
// native app already sends these events to GA through Firebase.

/** fintech-ui's GA4 property (its app/layout.tsx), so both CBCC flows report together. */
const GA_MEASUREMENT_ID = 'G-LJX9T6MDKX';

type GtagWindow = Window & {
	dataLayer?: unknown[];
	gtag?: (...args: unknown[]) => void;
};

/**
 * Loaded on the first CBCC event rather than app-wide, so gtag.js only loads
 * where CBCC events fire. The stub queues calls on `dataLayer` until the script
 * runs, so events fired while it downloads still go out. `config` also sends the
 * page_view; on the landing page the utm params are still in the URL then, so
 * GA can attribute the session.
 */
function loadGtag(w: GtagWindow): void {
	w.dataLayer = w.dataLayer || [];
	const { dataLayer } = w;
	w.gtag = function gtag() {
		// biome-ignore lint/complexity/noArguments: gtag.js only reads commands pushed as Arguments objects; a rest-param array is not treated as one.
		dataLayer.push(arguments);
	};
	w.gtag('js', new Date());
	w.gtag('config', GA_MEASUREMENT_ID);

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
	document.head.appendChild(script);
}

function toSnakeCase(input: string): string {
	return input
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
		.replace(/[^a-zA-Z0-9]+/g, '_')
		.toLowerCase()
		.replace(/^_+|_+$/g, '');
}

/** Send one CBCC event to GA4, with its name and param keys snake_cased. */
export function trackCbccGaEvent(
	name: string,
	properties: Record<string, unknown>,
): void {
	if (typeof window === 'undefined') return;
	if (isForeignEnvironment(getClientContext().clientConfig().clientId)) return;

	const w = window as GtagWindow;
	if (!w.gtag) loadGtag(w);
	const params = Object.fromEntries(
		Object.entries(properties).map(([key, value]) => [toSnakeCase(key), value]),
	);
	w.gtag?.('event', toSnakeCase(name), params);
}
